// PrivacyPolicy.js
import React from "react";
import { Phone, Mail, MapPin, ArrowRight } from "lucide-react";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Intro band */}
      <section className="bg-bnCream pt-16 pb-16 md:pt-24 md:pb-20">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-bnAccent text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
            Your Privacy
          </p>
          <h1 className="font-display font-light text-bnInk leading-[1.1] text-4xl md:text-5xl mb-5">
            Privacy Policy
          </h1>
          <p className="text-bnBody text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            Last Updated: February 2026
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 space-y-12">
          {/* Introduction */}
          <div>
            <p className="text-bnBody text-base leading-relaxed">
              At B & N Enterprise, protecting your privacy and keeping your
              personal information secure matters deeply to us. This Privacy
              Policy sets out how we collect, use, disclose, and safeguard your
              information when you use our services or visit our website. By
              engaging our services, you agree to the terms described in this
              policy.
            </p>
          </div>

          {/* Section 1 */}
          <div>
            <h2 className="font-display font-light text-bnInk leading-snug text-2xl md:text-3xl mb-4">
              1. Information We Collect
            </h2>
            <div className="space-y-4 text-bnBody text-base leading-relaxed">
              <p>
                We collect information that you provide directly to us,
                including but not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Personal Information:</strong> Name, address, phone
                  number, email address, date of birth, and emergency contact
                  details.
                </li>
                <li>
                  <strong>Health Information:</strong> Medical history, current
                  health conditions, medications, allergies, and care
                  preferences necessary to provide appropriate care services.
                </li>
                <li>
                  <strong>Financial Information:</strong> Payment details,
                  billing information, and insurance details for processing
                  payments.
                </li>
                <li>
                  <strong>Usage Information:</strong> Information about how you
                  interact with our website, including IP address, browser type,
                  pages visited, and time spent on pages.
                </li>
              </ul>
            </div>
          </div>

          {/* Section 2 */}
          <div>
            <h2 className="font-display font-light text-bnInk leading-snug text-2xl md:text-3xl mb-4">
              2. How We Use Your Information
            </h2>
            <div className="space-y-4 text-bnBody text-base leading-relaxed">
              <p>
                We use the information we collect for the following purposes:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Service Delivery:</strong> To provide, maintain, and
                  improve our care services, including matching you with
                  appropriate carers and developing personalised care plans.
                </li>
                <li>
                  <strong>Communication:</strong> To communicate with you about
                  appointments, care updates, billing, and respond to your
                  enquiries.
                </li>
                <li>
                  <strong>Safety & Quality:</strong> To ensure the safety and
                  quality of our services, including conducting background
                  checks on carers and monitoring care delivery.
                </li>
                <li>
                  <strong>Legal Compliance:</strong> To comply with applicable
                  laws, regulations, and legal processes.
                </li>
                <li>
                  <strong>Business Operations:</strong> To improve our website,
                  services, and internal operations, including analytics and
                  research.
                </li>
              </ul>
            </div>
          </div>

          {/* Section 3 */}
          <div>
            <h2 className="font-display font-light text-bnInk leading-snug text-2xl md:text-3xl mb-4">
              3. Information Sharing and Disclosure
            </h2>
            <div className="space-y-4 text-bnBody text-base leading-relaxed">
              <p>
                We do not sell, rent, or trade your personal information. We may
                share your information in the following circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Carers:</strong> We share relevant information with
                  assigned carers to enable them to provide appropriate care
                  services.
                </li>
                <li>
                  <strong>Healthcare Providers:</strong> With your consent, we
                  may share information with your doctors, nurses, therapists,
                  or other healthcare professionals involved in your care.
                </li>
                <li>
                  <strong>Service Providers:</strong> We may share information
                  with third-party service providers who assist us in operating
                  our business, such as payment processors, IT support, and
                  administrative services.
                </li>
                <li>
                  <strong>Legal Requirements:</strong> We may disclose
                  information when required by law, court order, or government
                  request, or to protect the rights, property, or safety of our
                  company, clients, or others.
                </li>
                <li>
                  <strong>Emergency Situations:</strong> In medical emergencies,
                  we may share information with emergency responders or
                  healthcare facilities.
                </li>
              </ul>
            </div>
          </div>

          {/* Section 4 */}
          <div>
            <h2 className="font-display font-light text-bnInk leading-snug text-2xl md:text-3xl mb-4">
              4. Data Security
            </h2>
            <div className="space-y-4 text-bnBody text-base leading-relaxed">
              <p>
                We implement appropriate technical and organisational measures
                to protect your personal information against unauthorised
                access, alteration, disclosure, or destruction. These measures
                include:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Secure storage of physical and electronic records</li>
                <li>Encryption of sensitive data during transmission</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls limiting who can view your information</li>
                <li>Staff training on privacy and security practices</li>
              </ul>
              <p>
                However, no method of transmission over the internet or
                electronic storage is 100% secure. While we strive to protect
                your information, we cannot guarantee absolute security.
              </p>
            </div>
          </div>

          {/* Section 5 */}
          <div>
            <h2 className="font-display font-light text-bnInk leading-snug text-2xl md:text-3xl mb-4">
              5. Your Rights and Choices
            </h2>
            <div className="space-y-4 text-bnBody text-base leading-relaxed">
              <p>
                You have the following rights regarding your personal
                information:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Access:</strong> You may request access to the
                  personal information we hold about you.
                </li>
                <li>
                  <strong>Correction:</strong> You may request correction of
                  inaccurate or incomplete information.
                </li>
                <li>
                  <strong>Deletion:</strong> You may request deletion of your
                  personal information, subject to legal and contractual
                  obligations.
                </li>
                <li>
                  <strong>Restriction:</strong> You may request that we restrict
                  the processing of your information in certain circumstances.
                </li>
                <li>
                  <strong>Objection:</strong> You may object to the processing
                  of your information for certain purposes.
                </li>
                <li>
                  <strong>Portability:</strong> You may request a copy of your
                  information in a structured, commonly used format.
                </li>
              </ul>
              <p>
                To exercise any of these rights, please contact us using the
                details provided below.
              </p>
            </div>
          </div>

          {/* Section 6 */}
          <div>
            <h2 className="font-display font-light text-bnInk leading-snug text-2xl md:text-3xl mb-4">
              6. Retention of Information
            </h2>
            <div className="space-y-4 text-bnBody text-base leading-relaxed">
              <p>
                We retain your personal information for as long as necessary to
                fulfil the purposes outlined in this Privacy Policy, unless a
                longer retention period is required or permitted by law. When
                information is no longer needed, we securely delete or anonymise
                it in accordance with our data retention policies and applicable
                regulations.
              </p>
            </div>
          </div>

          {/* Section 7 */}
          <div>
            <h2 className="font-display font-light text-bnInk leading-snug text-2xl md:text-3xl mb-4">
              7. Children's Privacy
            </h2>
            <div className="space-y-4 text-bnBody text-base leading-relaxed">
              <p>
                Our services are not directed to individuals under the age of
                18. We do not knowingly collect personal information from
                children. If you believe we have inadvertently collected
                information from a child, please contact us immediately so we
                can delete it.
              </p>
            </div>
          </div>

          {/* Section 8 */}
          <div>
            <h2 className="font-display font-light text-bnInk leading-snug text-2xl md:text-3xl mb-4">
              8. Third-Party Links
            </h2>
            <div className="space-y-4 text-bnBody text-base leading-relaxed">
              <p>
                Our website may contain links to third-party websites. We are
                not responsible for the privacy practices or content of these
                external sites. We encourage you to review the privacy policies
                of any third-party sites you visit.
              </p>
            </div>
          </div>

          {/* Section 9 */}
          <div>
            <h2 className="font-display font-light text-bnInk leading-snug text-2xl md:text-3xl mb-4">
              9. Changes to This Privacy Policy
            </h2>
            <div className="space-y-4 text-bnBody text-base leading-relaxed">
              <p>
                We may update this Privacy Policy from time to time to reflect
                changes in our practices, technology, legal requirements, or
                other factors. We will notify you of any material changes by
                posting the updated policy on our website with a new "Last
                Updated" date. Your continued use of our services after such
                changes constitutes your acceptance of the updated policy.
              </p>
            </div>
          </div>

          {/* Section 10 */}
          <div>
            <h2 className="font-display font-light text-bnInk leading-snug text-2xl md:text-3xl mb-4">
              10. Australian Privacy Principles
            </h2>
            <div className="space-y-4 text-bnBody text-base leading-relaxed">
              <p>
                B & N Enterprise is committed to complying with the Australian
                Privacy Principles (APPs) contained in the Privacy Act 1988
                (Cth). We handle personal information in accordance with these
                principles and applicable Australian privacy laws.
              </p>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-bnCream border border-bnBorder p-8 rounded-2xl">
            <h2 className="font-display font-light text-bnInk leading-snug text-2xl md:text-3xl mb-6">
              Contact Us
            </h2>
            <div className="space-y-4 text-bnBody text-base leading-relaxed">
              <p>
                If you have any questions, concerns, or requests regarding this
                Privacy Policy or our privacy practices, please contact us:
              </p>

              <div className="space-y-3 mt-6">
                <div className="flex items-start gap-3">
                  <MapPin
                    size={20}
                    className="text-bnAccent flex-shrink-0 mt-1"
                  />
                  <div>
                    <p className="font-medium text-bnInk">B & N Enterprise</p>
                    <p>ABN 21 001 726 763</p>
                    <p>23 Mahony Ct</p>
                    <p>Weston ACT 2611</p>
                    <p>Australia</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Phone size={20} className="text-bnAccent flex-shrink-0" />
                  <a
                    href="tel:+61480804640"
                    className="text-bnAccent hover:text-bnAccentDark font-medium transition-colors"
                  >
                    +61 480 804 640
                  </a>
                </div>

                <div className="flex items-center gap-3">
                  <Mail size={20} className="text-bnAccent flex-shrink-0" />
                  <a
                    href="mailto:support@bnenterprise.care"
                    className="text-bnAccent hover:text-bnAccentDark font-medium transition-colors"
                  >
                    support@bnenterprise.care
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-bnCream py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-bnAccent text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
            Trust and Transparency
          </p>
          <h2 className="font-display font-light text-bnInk leading-[1.1] text-3xl md:text-4xl mb-5">
            Your Privacy Matters to Us
          </h2>
          <p className="text-bnBody text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-10">
            We are committed to protecting your personal information and
            providing transparent, compassionate care.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center justify-center gap-2 bg-bnAccent hover:bg-bnAccentDark text-white font-medium text-sm px-7 py-3.5 rounded-full transition-colors"
          >
            Contact Us
            <ArrowRight size={16} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
